import { useEffect, useMemo, useRef, useState } from 'react';
import Page from '@/components/Page';
import { Button, Container, Grid, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { makeStyles } from '@mui/styles';
import { MIconButton } from '@/@material-extend';

import {
  StaffNote,
  NOTE_ACTIONS,
  BaseNote,
  AppointmentNote,
  CustomerNote
} from './Types';
import ListSortFilter, {
  getNoteFilterFn,
  getSortOnFieldFn,
  ListFilterOption,
  ListSortOption
} from './ListSortFilter';
import { formDataInstance } from '@/common/axios';
import CONFIG from '@/config';
import AddEditNoteModal from './AddEditNoteModal';
import DeleteNoteConfirmModal from './DeleteNoteConfirmModal';
import NoteList from './NoteList';
import { SafetyNoteItem } from './SafetyNoteItem';
import { getErrorDetails } from './ApiErrorLookup';
import { getRestHeaders } from '@/common/utils/api';
import { useCustomerNoteEnums } from '@/hooks/useCustomerNoteEnums';

const useStyles: any = makeStyles(() => ({
  root: {
    overflowY: 'auto',
    overflowX: 'hidden'
  },
  container: {
    paddingTop: 0,
    marginTop: 0
  },
  header: {
    padding: 0,
    height: 42
  },
  headerTitle: {
    color: '#000000',
    fontSize: '16px',
    fontWeight: 600
  },
  headerButton: {
    backgroundColor: '#055577',
    color: '#FFFFFF',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: '#042E43'
    }
  },
  collapseExpandAll: {
    color: '#055577',
    height: 56,
    fontSize: 14,
    lineHeight: '56px',
    paddingLeft: 24,
    width: 140,
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  },
  body: {
    padding: '8px 0'
  },
  tableContainer: {
    display: 'flex'
  },
  userCount: {
    fontSize: '14px',
    fontWeight: 600,
    width: 200,
    display: 'inline-block',
    whiteSpace: 'nowrap'
  },
  selectCountAndSortFilter: {
    height: 28,
    display: 'flex',
    justifyContent: 'space-between'
  },
  table: {
    '& .MuiDataGrid-virtualScrollerRenderZone': {
      height: 'calc(100vh - 194px) !important',
      overflowY: 'auto',
      overflowX: 'hidden',
      position: 'relative'
    }
  }
}));

export const pinNoteApiSuffix = '/staff/pin-note';
export const getSafetyNoteApiSuffix = '/staff/user-safety-alert';
export const getNotesApiSuffix = '/staff/user-notes-alert';

export interface PinNoteApiResponse {
  data: {
    data: {
      success: boolean;
    };
  };
}

export interface NoteListingApiResponse {
  data: {
    data: {
      appointmentNotes: AppointmentNote[];
      customerNotes: StaffNote[];
      safetyAlert: BaseNote;
    };
  };
}

export interface SafetyNoteApiResponse {
  data: {
    data: {
      safetyNote: BaseNote;
    };
  };
}

export const addViewStates = (notes: CustomerNote[]): CustomerNote[] =>
  notes.map(note => ({
    ...note,
    expanded: false,
    showComments: false,
    pinLoading: false
  }));

export const sortSelections: ListSortOption[] = [
  {
    value: 100,
    display: 'Date Created (Newest)',
    sortFn: getSortOnFieldFn('createdAt', -1)
  },
  {
    value: 200,
    display: 'Date Created (Oldest)',
    sortFn: getSortOnFieldFn('createdAt', 1)
  },
  {
    value: 300,
    display: 'Created By (A-Z)',
    sortFn: getSortOnFieldFn('createdBy', -1)
  },
  {
    value: 400,
    display: 'Created By (Z-A)',
    sortFn: getSortOnFieldFn('createdBy', 1)
  }
];

export interface NewAlertsNotesProps {
  customer: {
    id: number;
    firstName: string;
    lastName: string;
  };
}

const AlertsNotes = (props: NewAlertsNotesProps) => {
  const { customer } = props;

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const classes = useStyles();

  const { enums, loading: loadingEnums } = useCustomerNoteEnums();
  const filterSelections = useMemo(() => {
    if (enums) {
      return enums?.noteCategories?.map(c => {
        return {
          value: c.id,
          display: c.name,
          filterFn: getNoteFilterFn(c.id)
        };
      });
    } else {
      return [];
    }
  }, [loadingEnums]);

  const [addEditNoteModalOpen, setAddEditNoteModalOpen] = useState(false);
  const [confirmNoteDeleteModalOpen, setConfirmNoteDeleteModalOpen] =
    useState(false);
  const [notesLoading, setNotesLoading] = useState(false);
  const [noteCount, setNoteCount] = useState(0);
  const [filterSelection, setFilterSelection] =
    useState<ListFilterOption | null>(null);
  const [sortSelection, setSortSelection] = useState<ListSortOption>(null);

  // This is the source of truth for all customer nodes from this component down
  const [noteList, setNoteList] = useState<CustomerNote[]>([]); // noteList the list of notes
  const [noteListView, setNoteListView] = useState<CustomerNote[]>([]); // noteListView is the filtered/sorted list of notes
  const [selectedNoteForUserAction, setSelectedNoteForUserAction] =
    useState<StaffNote | null>(null); // selectedNoteForUserAction is the note that is the current target of a mutating action (edit/delete)

  // used for working around staffList being stale in funciton closures around the useEffect functions
  const noteListRef = useRef<CustomerNote[]>(noteList);

  useEffect(() => {
    noteListRef.current = noteList;
  }, [noteList]);

  // if list, filter or sort change update the visible list
  useEffect(() => {
    let sortedFilteredList = [...noteList.filter(i => !i.isPinned)];
    const pinnedNote = noteList.filter(i => i.isPinned);
    if (sortSelection !== null) {
      sortedFilteredList = sortedFilteredList.sort(sortSelection.sortFn);
    }
    if (filterSelection !== null) {
      sortedFilteredList = sortedFilteredList.filter(filterSelection.filterFn);
    }
    if (pinnedNote) {
      sortedFilteredList = [...pinnedNote, ...sortedFilteredList];
    }
    setNoteListView(sortedFilteredList);
    setNoteCount(sortedFilteredList.length);
  }, [noteList, sortSelection, filterSelection]);

  useEffect(() => {
    setNotesLoading(true);

    formDataInstance
      .get(
        `${CONFIG.USERS_API_URL}${getNotesApiSuffix}?customerId=${customer.id}`
      )
      .then((response: NoteListingApiResponse) => {
        const notes = [
          ...response.data.data.customerNotes,
          ...response.data.data.appointmentNotes.map(n => ({
            ...n,
            firstName: customer.firstName,
            lastName: customer.lastName
          }))
        ].slice(0, 400);
        setNoteList(addViewStates(notes));
        setNoteCount(notes.length);
      })
      .catch(error =>
        enqueueSnackbar(
          getErrorDetails(`There was a problem getting Notes.`, error),
          {
            variant: 'error',
            action: (key: any) => (
              <MIconButton size="small" onClick={() => closeSnackbar(key)} />
            )
          }
        )
      )
      .finally(() => setNotesLoading(false));
  }, []);

  const collapseAll = () => {
    setNoteList(
      noteListRef.current.map(n => ({
        ...n,
        expanded: false,
        showComments: false
      }))
    );
  };

  // Command Action Dispatch: operations are triggered by various child components of
  // this one and dispatched to this common place.  It is not important where the HTTP
  // calls are made, as long as the result of those api calls update the notes state in
  // this component.
  const triggerNoteAction = (
    note: StaffNote,
    action: keyof typeof NOTE_ACTIONS,
    data?: any
  ) => {
    const nl = noteListRef.current;
    const index = nl.findIndex(s => s.id == note.id);
    const nextList = [...nl];

    switch (action) {
      case NOTE_ACTIONS.EXPAND_NOTE_VIEW: {
        nextList[index] = { ...note, expanded: true };
        setNoteList(nextList);
        break;
      }
      case NOTE_ACTIONS.COLLAPSE_NOTE_VIEW: {
        nextList[index] = { ...note, expanded: false, showComments: false };
        setNoteList(nextList);
        break;
      }
      case NOTE_ACTIONS.PIN_NOTE: {
        nextList[index] = { ...note, pinLoading: true };
        setNoteList(nextList);
        formDataInstance
          .post(
            CONFIG.USERS_API_URL + pinNoteApiSuffix,
            {
              customerId: customer.id,
              noteId: note.id,
              type: note.type
            },
            {
              headers: getRestHeaders()
            }
          )
          .then((response: PinNoteApiResponse) => {
            if (response.data.data.success) {
              const unpinnedNoteList: CustomerNote[] = nextList.map(n => ({
                ...n,
                isPinned: false
              }));
              unpinnedNoteList[index] = {
                ...note,
                isPinned: true
              };
              setNoteList(unpinnedNoteList);
            }
          })
          .catch(error => {
            enqueueSnackbar(
              getErrorDetails(
                `There was a problem setting Note as isPinned.`,
                error
              ),
              {
                variant: 'error',
                action: (key: any) => (
                  <MIconButton
                    size="small"
                    onClick={() => closeSnackbar(key)}
                  />
                )
              }
            );
          });
        break;
      }
      case NOTE_ACTIONS.UNPIN_NOTE: {
        nextList[index] = { ...note, pinLoading: true };
        setNoteList(nextList);
        formDataInstance
          .delete(
            `${CONFIG.USERS_API_URL}${pinNoteApiSuffix}/?customerId=${customer.id}&noteId=${note.id}&type=${note.type}`,
            {
              headers: getRestHeaders()
            }
          )
          .then((response: PinNoteApiResponse) => {
            if (response.data.data.success) {
              const unpinnedNoteList: CustomerNote[] = nextList.map(n => ({
                ...n,
                isPinned: false,
                pinLoading: false
              }));
              setNoteList(unpinnedNoteList);
            }
          })
          .catch(error => {
            enqueueSnackbar(
              getErrorDetails(
                `There was a problem setting Note as unisPinned.`,
                error
              ),
              {
                variant: 'error',
                action: (key: any) => (
                  <MIconButton
                    size="small"
                    onClick={() => closeSnackbar(key)}
                  />
                )
              }
            );
          });
        break;
      }
      case NOTE_ACTIONS.ADD_NOTE: {
        setSelectedNoteForUserAction(null);
        setAddEditNoteModalOpen(true);
        break;
      }
      case NOTE_ACTIONS.INSERT_NOTE: {
        setSelectedNoteForUserAction(null);
        nextList.push(note);
        setNoteList(nextList);
        break;
      }
      case NOTE_ACTIONS.EDIT_NOTE: {
        setSelectedNoteForUserAction(note);
        setAddEditNoteModalOpen(true);
        break;
      }
      case NOTE_ACTIONS.UPDATE_NOTE: {
        setSelectedNoteForUserAction(null);
        // keeping the expanded and showComments state of the prior instance of the edited note
        const priorNote = nextList[index];
        nextList[index] = {
          ...note,
          expanded: priorNote.expanded,
          showComments: priorNote.showComments
        };
        setNoteList(nextList);
        break;
      }
      case NOTE_ACTIONS.DELETE_NOTE: {
        setSelectedNoteForUserAction(note);
        setConfirmNoteDeleteModalOpen(true);
        break;
      }
      case NOTE_ACTIONS.REMOVE_NOTE: {
        setSelectedNoteForUserAction(null);
        setNoteList(nextList.filter(n => n.id !== note.id));
        break;
      }
      case NOTE_ACTIONS.INSERT_COMMENT: {
        // data is the new comment
        nextList[index] = {
          ...note,
          showComments: true,
          comments: [...note.comments, data]
        } as StaffNote;
        setNoteList(nextList);
        break;
      }
      case NOTE_ACTIONS.REMOVE_COMMENT: {
        // data is the comment id to remove
        nextList[index] = {
          ...note,
          comments: [...note.comments.filter(c => c.id !== data)]
        } as StaffNote;
        setNoteList(nextList);
        break;
      }
      case NOTE_ACTIONS.SHOW_COMMENTS: {
        nextList[index] = { ...note, showComments: true };
        setNoteList(nextList);
        break;
      }
      case NOTE_ACTIONS.HIDE_COMMENTS: {
        nextList[index] = { ...note, showComments: false };
        setNoteList(nextList);
        break;
      }
      case NOTE_ACTIONS.UPDATE_NOTE_APPOINTMENT: {
        // data is the new appointment
        nextList[index] = {
          ...note,
          content: data.notes,
          start: data.start,
          end: data.end,
          service: {
            id: data?.purchasableItem?.id,
            description: data?.purchasableItem?.description,
            category: data?.purchasableItem?.purchaseCategory?.id
          },
          updatedAt: data.updatedAt
        };
        setNoteList(nextList);
        break;
      }
    }
  };

  return (
    <Page title="Client Notes" className={classes.root}>
      <Container maxWidth="xl">
        <Grid container className={classes.header}>
          <Grid item xs={6} className="section-heading">
            <Typography className={classes.userCount}>
              {noteCount} note{noteCount > 1 ? 's' : ''}
            </Typography>
            <ListSortFilter
              setFilterSelection={setFilterSelection}
              setSortSelection={setSortSelection}
              filterSelections={filterSelections}
              sortSelections={sortSelections}
            />
            <div className={classes.collapseExpandAll} onClick={collapseAll}>
              Collapse All
            </div>
          </Grid>
          <Grid
            item
            className={classes.header}
            xs={6}
            display="flex"
            justifyContent="flex-end"
          >
            <Button
              variant="contained"
              color="primary"
              className={classes.headerButton}
              onClick={() => setAddEditNoteModalOpen(true)}
            >
              Add Note
            </Button>
          </Grid>
        </Grid>
        <Grid container className={classes.body}>
          <Grid container className={classes.tableContainer}>
            <Grid item xs={12} className={classes.table}>
              <SafetyNoteItem customerId={customer.id} />
              <NoteList
                noteListData={noteListView}
                loading={loadingEnums || notesLoading}
                actionCallback={triggerNoteAction}
              />
            </Grid>
          </Grid>
        </Grid>
      </Container>
      {addEditNoteModalOpen && (
        <AddEditNoteModal
          isOpen={addEditNoteModalOpen}
          setIsOpen={open => {
            setAddEditNoteModalOpen(open);
            setSelectedNoteForUserAction(null);
          }}
          note={selectedNoteForUserAction}
          customerId={customer.id}
          actionCallback={triggerNoteAction}
        />
      )}
      {confirmNoteDeleteModalOpen && (
        <DeleteNoteConfirmModal
          isOpen={confirmNoteDeleteModalOpen}
          setIsOpen={open => {
            setConfirmNoteDeleteModalOpen(open);
            setSelectedNoteForUserAction(null);
          }}
          note={selectedNoteForUserAction}
          actionCallback={triggerNoteAction}
        />
      )}
    </Page>
  );
};

export default AlertsNotes;
